/**
 * Polling for a bulk contact upload's processing status.
 *
 * Large uploads can run for many minutes, so polling only gives up when the
 * upload stops making progress (or after a generous overall cap) instead of
 * after a fixed number of attempts. Giving up does not mean the upload
 * failed: the backend keeps processing, so callers get an
 * `UploadStillProcessingError` rather than a failure.
 */

export interface PolledUploadStatus {
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number | null;
}

export class UploadStillProcessingError extends Error {
  constructor() {
    super('Your upload is still processing. Contacts will keep appearing as it finishes — check back in a few minutes.');
    this.name = 'UploadStillProcessingError';
  }
}

export interface PollUploadOptions {
  intervalMs?: number;
  /** Give up when status and progress have not changed for this long. */
  stallTimeoutMs?: number;
  /** Give up after this long regardless of progress. */
  maxDurationMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const pollUntilSettled = async <T extends PolledUploadStatus>(
  fetchStatus: () => Promise<T>,
  onStatus?: (status: T) => void,
  {
    intervalMs = 2000,
    stallTimeoutMs = 3 * 60 * 1000,
    maxDurationMs = 60 * 60 * 1000,
    now = Date.now,
    sleep = defaultSleep,
  }: PollUploadOptions = {}
): Promise<T> => {
  const startedAt = now();
  let lastChangeAt = startedAt;
  let lastKey: string | null = null;

  for (;;) {
    const status = await fetchStatus();
    onStatus?.(status);
    if (status.status === 'completed' || status.status === 'failed') {
      return status;
    }

    const key = `${status.status}:${status.progress ?? ''}`;
    const checkedAt = now();
    if (key !== lastKey) {
      lastKey = key;
      lastChangeAt = checkedAt;
    }
    if (checkedAt - lastChangeAt >= stallTimeoutMs || checkedAt - startedAt >= maxDurationMs) {
      throw new UploadStillProcessingError();
    }
    await sleep(intervalMs);
  }
};
